/*
  Kiwi Ears · series "How it sounds" chart — Figma 390:255 (Rene 2026-08-21).

  Same engine as assets/pk-tuning.js and cs-signature.js (log-x 20Hz–20kHz,
  40–81dB, Catmull-Rom smoothing, fixed plot aspect at every width, Hz-label
  collision sweep), but this node's own palette:
    · plot field  — green radial from the centre, #00FF99 → #00D47F
    · curve       — black, 4px, 8/8 dash
    · grid        — black 0.2 minor / 0.4 major verticals, black 0.2 interior
                    rules, solid black top and bottom
    · axis labels — white (the section sits on the night→purple gradient)
  Plot aspect here is the node's own 461/968; it stays constant across
  breakpoints, per the locked audio-graph rule.

  Compare: the pill lists every model in the series and overlays the picked one
  as a WHITE dashed curve. Selecting also fires kiwi:series-compare on document
  so the compare modal can take over the interaction later without touching this
  file.
*/
(function () {
  var mount = document.getElementById('cs-sound-chart');
  if (!mount || !window.d3) return;
  var d3 = window.d3;

  var CURVE = '#000000', COMPARE = '#FFFFFF';
  var FIELD_IN = '#00FF99', FIELD_OUT = '#00D47F';
  var FONT_SANS = "'Robout',sans-serif";
  var FONT_MONO = "'JetBrains Mono',ui-monospace,monospace";
  var PLOT_ASPECT = 461 / 968;

  var X_TICKS = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
  var X_TICKS_SMALL = [20, 100, 500, 1000, 2000, 10000, 20000];
  var MAJOR = [50, 100, 200, 2000, 5000, 10000];

  var FALLBACK = [[20, 70.5], [40, 69.7], [80, 67.6], [140, 65.6], [200, 64.4], [320, 62.2], [500, 61], [800, 61.8], [1300, 64.8], [2000, 68.8], [3000, 73.2], [4200, 70.2], [5700, 71.8], [7000, 64.8], [9500, 66.8], [12000, 64.6], [15000, 61.2], [17000, 51], [20000, 52.4]];

  function fmtHz(v, small) {
    if (small) return v >= 1000 ? (v / 1000) + 'k' : '' + v;
    return v >= 1000 ? (v / 1000) + 'k' + (v === 20000 ? 'Hz' : '') : v + (v === 20 ? 'Hz' : '');
  }
  function parseCurveText(raw) {
    if (typeof raw !== 'string') return null;
    var out = [];
    raw.split(/[\r\n]+/).forEach(function (line) {
      line = line.trim();
      if (!line || /^[#;*/]/.test(line)) return;
      var cols = line.split(/[\s,;\t]+/).filter(Boolean);
      if (cols.length < 2) return;
      var hz = parseFloat(cols[0]), db = parseFloat(cols[1]);
      if (isFinite(hz) && isFinite(db) && hz > 0) out.push([hz, db]);
    });
    return out.length > 2 ? out : null;
  }

  var MODELS = [];
  var payload = document.getElementById('cs-sound-data');
  if (payload) {
    try {
      (JSON.parse(payload.textContent) || []).forEach(function (m) {
        var pts = parseCurveText(m.text);
        if (pts) MODELS.push({ name: m.name, url: m.url, img: m.img, pts: pts });
      });
    } catch (e) { /* bad metafield — fall through to the house curve */ }
  }
  var MAIN = MODELS.length ? MODELS[0].pts : FALLBACK;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var drawn = false, compare = null;
  var svg, x, y, plotW, plotH, mainPath, cmpPath, cmpDash;

  function lineGen() {
    return d3.line()
      .x(function (d) { return x(d[0]); })
      .y(function (d) { return y(d[1]); })
      .curve(d3.curveCatmullRom.alpha(0.6));
  }

  function build() {
    mount.innerHTML = '';
    var W = mount.clientWidth;
    if (!W) return;
    var isSmall = W < 560;   /* the node shows the full 20Hz…20kHz set on desktop */
    var m = { top: 6, right: 6, bottom: isSmall ? 24 : 32, left: 6 };
    plotW = W - m.left - m.right;
    plotH = Math.round(plotW * PLOT_ASPECT);
    var H = plotH + m.top + m.bottom;
    var k = plotW / 968;

    x = d3.scaleLog().domain([20, 20000]).range([0, plotW]);
    y = d3.scaleLinear().domain([40, 81]).range([plotH, 0]);

    svg = d3.select(mount).append('svg')
      .attr('width', W).attr('height', H)
      .attr('viewBox', '0 0 ' + W + ' ' + H)
      .attr('role', 'img')
      .attr('aria-label', 'Frequency response of this series');
    var g = svg.append('g').attr('transform', 'translate(' + m.left + ',' + m.top + ')');

    /* green radial field, brightest dead centre (Figma 398:159) */
    var field = svg.append('defs').append('radialGradient')
      .attr('id', 'cssnd-field').attr('gradientUnits', 'userSpaceOnUse')
      .attr('cx', plotW / 2).attr('cy', plotH / 2)
      .attr('r', plotW / 2);
    field.append('stop').attr('offset', 0).attr('stop-color', FIELD_IN);
    field.append('stop').attr('offset', 1).attr('stop-color', FIELD_OUT);
    g.append('rect').attr('width', plotW).attr('height', plotH).attr('fill', 'url(#cssnd-field)');

    /* grid */
    var minors = x.ticks(40).filter(function (v) { return v >= 20 && v <= 20000; });
    g.selectAll('.grid-v').data(minors).enter().append('line')
      .attr('x1', function (d) { return x(d); }).attr('x2', function (d) { return x(d); })
      .attr('y1', 0).attr('y2', plotH)
      .attr('stroke', function (d) { return MAJOR.indexOf(d) > -1 ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)'; })
      .attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
    [80, 70, 60, 50].forEach(function (v) {
      g.append('line').attr('x1', 0).attr('x2', plotW).attr('y1', y(v)).attr('y2', y(v))
        .attr('stroke', 'rgba(0,0,0,0.2)').attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
    });
    [0, plotH].forEach(function (yy) {
      g.append('line').attr('x1', 0).attr('x2', plotW).attr('y1', yy).attr('y2', yy)
        .attr('stroke', '#000').attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
    });

    /* Hz labels — white here, same collision sweep as the PDP chart */
    var hz = (isSmall ? X_TICKS_SMALL : X_TICKS).map(function (v) {
      return svg.append('text').attr('x', m.left + x(v)).attr('y', H - 6)
        .attr('text-anchor', v === 20 ? 'start' : v === 20000 ? 'end' : 'middle')
        .attr('font-family', FONT_SANS).attr('font-size', isSmall ? 10 : 14)
        .attr('font-weight', 600).attr('fill', '#FFFFFF')
        .text(fmtHz(v, isSmall)).node();
    });
    (function cull() {
      var PAD = isSmall ? 2 : 6;
      var b = hz.map(function (n) { var r = n.getBBox(); return { node: n, x0: r.x, x1: r.x + r.width }; });
      if (!b.length) return;
      var last = b[b.length - 1], prev = b[0];
      for (var i = 1; i < b.length - 1; i++) {
        if (b[i].x0 < prev.x1 + PAD || b[i].x1 + PAD > last.x0) b[i].node.remove();
        else prev = b[i];
      }
    })();

    var dash = Math.max(4, 8 * k);
    cmpDash = dash + ' ' + dash;
    cmpPath = g.append('path').attr('fill', 'none')
      .attr('stroke', COMPARE).attr('stroke-width', Math.max(2, 4 * k))
      .attr('stroke-dasharray', cmpDash).attr('stroke-linecap', 'round').attr('opacity', 0);
    if (compare) cmpPath.attr('d', lineGen()(compare.pts)).attr('opacity', 1);

    mainPath = g.append('path').attr('fill', 'none')
      .attr('stroke', CURVE).attr('stroke-width', Math.max(2, 4 * k))
      .attr('stroke-dasharray', cmpDash).attr('stroke-linecap', 'round')
      .attr('d', lineGen()(MAIN));

    if (!reduceMotion && !drawn && window.gsap && window.ScrollTrigger) {
      /* the dash pattern IS the design, so reveal by clipping rather than by
         animating stroke-dashoffset (which would eat the dashes) */
      var clip = svg.append('defs').append('clipPath').attr('id', 'cssnd-reveal')
        .append('rect').attr('x', 0).attr('y', 0).attr('width', 0).attr('height', plotH);
      mainPath.attr('clip-path', 'url(#cssnd-reveal)');
      window.gsap.to(clip.node(), {
        attr: { width: plotW }, duration: 1.5, ease: 'power2.inOut',
        scrollTrigger: { trigger: mount, start: 'top 82%', once: true },
        onComplete: function () { drawn = true; mainPath.attr('clip-path', null); }
      });
    } else { drawn = true; }

    /* crosshair readout */
    var hover = g.append('g').style('display', 'none');
    hover.append('line').attr('y1', 0).attr('y2', plotH)
      .attr('stroke', 'rgba(0,0,0,0.45)').attr('stroke-dasharray', '3,3');
    var dotM = hover.append('circle').attr('r', 4).attr('fill', '#000').attr('stroke', FIELD_IN).attr('stroke-width', 1.5);
    var dotC = hover.append('circle').attr('r', 4).attr('fill', '#FFF').attr('stroke', '#000').attr('stroke-width', 1.5).style('display', 'none');
    var tip = hover.append('text').attr('font-family', FONT_MONO).attr('font-size', 12)
      .attr('fill', '#0B1F16').attr('stroke', 'rgba(255,255,255,0.7)').attr('stroke-width', 3)
      .attr('paint-order', 'stroke').attr('stroke-linejoin', 'round');
    var bisect = d3.bisector(function (d) { return d[0]; }).left;
    function sample(pts, f) {
      var i = Math.min(Math.max(bisect(pts, f), 1), pts.length - 1);
      var a = pts[i - 1], b = pts[i];
      var t = (Math.log(f) - Math.log(a[0])) / (Math.log(b[0]) - Math.log(a[0]));
      return a[1] + t * (b[1] - a[1]);
    }
    svg.append('rect')
      .attr('x', m.left).attr('y', m.top).attr('width', plotW).attr('height', plotH)
      .attr('fill', 'transparent')
      .on('mouseenter', function () { hover.style('display', null); })
      .on('mouseleave', function () { hover.style('display', 'none'); })
      .on('mousemove', function (ev) {
        var px = d3.pointer(ev, this)[0] - m.left;
        var f = Math.min(20000, Math.max(20, x.invert(px)));
        var mv = sample(MAIN, f);
        hover.select('line').attr('x1', px).attr('x2', px);
        dotM.attr('cx', px).attr('cy', y(mv));
        var lbl = (f >= 1000 ? (f / 1000).toFixed(1) + 'kHz' : Math.round(f) + 'Hz') + ' · ' + mv.toFixed(1) + 'dB';
        if (compare) {
          var cv = sample(compare.pts, f);
          dotC.style('display', null).attr('cx', px).attr('cy', y(cv));
          lbl += ' / ' + cv.toFixed(1) + 'dB';
        } else dotC.style('display', 'none');
        tip.attr('x', Math.min(px + 10, plotW - 170)).attr('y', Math.max(y(mv) - 12, 14)).text(lbl);
      });
  }

  /* ---- compare pill ---- */
  (function () {
    var btn = document.getElementById('cs-sound-cmp');
    var list = document.getElementById('cs-sound-cmplist');
    var label = document.getElementById('cs-sound-cmplabel');
    var swatch = document.getElementById('cs-sound-cmpsw');
    if (!btn || !list) return;
    var base = label ? label.textContent : '';

    function close() { list.classList.add('hidden'); btn.setAttribute('aria-expanded', 'false'); }
    function open() {
      list.classList.remove('hidden');
      btn.setAttribute('aria-expanded', 'true');
      if (window.gsap && !reduceMotion) window.gsap.fromTo(list, { opacity: 0, y: -6 }, { opacity: 1, y: 0, duration: 0.18, ease: 'power2.out' });
    }
    function pick(mo) {
      compare = mo;
      if (label) label.textContent = mo ? mo.name : base;
      if (swatch) {
        swatch.innerHTML = '';
        if (mo && mo.img) {
          var im = document.createElement('img');
          im.src = mo.img; im.alt = '';
          im.className = 'h-full w-full object-cover';
          swatch.appendChild(im);
        } else { swatch.textContent = 'PK'; }
      }
      if (cmpPath) {
        if (mo) cmpPath.attr('d', lineGen()(mo.pts)).attr('stroke-dasharray', cmpDash).attr('opacity', 1);
        else cmpPath.attr('opacity', 0);
      }
      /* the compare modal will listen for this and can drive pick() itself */
      document.dispatchEvent(new CustomEvent('kiwi:series-compare', { detail: { model: mo, models: MODELS } }));
      close();
    }
    function render() {
      list.innerHTML = '';
      if (compare) {
        var clr = document.createElement('li');
        clr.className = 'pk-fr-item pk-fr-clear';
        clr.textContent = '✕ Clear comparison';
        clr.addEventListener('click', function () { pick(null); });
        list.appendChild(clr);
      }
      var others = MODELS.filter(function (mo) { return mo.pts !== MAIN; });
      if (!others.length) {
        var n = document.createElement('li');
        n.className = 'pk-fr-note';
        n.textContent = 'No other model in this series has tuning data yet.';
        list.appendChild(n);
        return;
      }
      others.forEach(function (mo) {
        var li = document.createElement('li');
        li.className = 'pk-fr-item';
        li.textContent = mo.name;
        li.addEventListener('click', function () { pick(mo); });
        list.appendChild(li);
      });
    }
    btn.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      if (list.classList.contains('hidden')) { render(); open(); } else close();
    });
    document.addEventListener('click', function (e) {
      if (!list.contains(e.target) && !btn.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    /* let a future modal drive the selection: dispatch kiwi:series-compare-set */
    document.addEventListener('kiwi:series-compare-set', function (e) {
      var want = e.detail && e.detail.name;
      var found = null;
      MODELS.forEach(function (mo) { if (mo.name === want) found = mo; });
      pick(found);
    });
  })();

  build();
  var t;
  if ('ResizeObserver' in window) new ResizeObserver(function () { clearTimeout(t); t = setTimeout(build, 150); }).observe(mount);
  else window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(build, 150); });
})();
